import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../utils/LanguageUtils';
import HelptextConstantsEnum from '../../constants/helptextConstantsEnum';
import TableUtils from '../../utils/tableUtils';
import DataGridComponent from '../common/dataGrid';
import UrlConstants from '../../constants/UrlConstants';
import CustomStringLink from '../common/customStringLink';

interface CancellationRuleListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    cancellationRule: LanguageUtils.createMessage('Cancellation rule'),
    priorityRank: LanguageUtils.createMessage('Priority rank'),
    details: LanguageUtils.createMessage('Cancellation rule details'),
    id: LanguageUtils.createMessage('Cancellation rule number'),
    cancellationType: LanguageUtils.createMessage('Cancellation type')
};

const CancellationRuleList = forwardRef((props: CancellationRuleListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_KICKBACK_POSTEN);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'id',
                headerName: intl.formatMessage(messages.id),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(cellValues)?.id}
                        link={`/#/${UrlConstants.CANCELLATION_RULE}/cancellationRuleDetails/${applyRowFn(cellValues)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('fromTerminal', hiddenColumns)
            },
            {
                field: 'cancellationRuleText',
                headerName: intl.formatMessage(messages.details),
                flex: 3,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <CustomStringLink value={applyRowFn(cellValues)?.cancellationRuleText}
                        link={`/#/${UrlConstants.CANCELLATION_RULE}/cancellationRuleDetails/${applyRowFn(cellValues)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('cancellationRuleText', hiddenColumns)
            },
            {
                field: 'priorityRank',
                headerName: intl.formatMessage(messages.priorityRank),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(cellValues)?.priorityRank}
                        link={`/#/${UrlConstants.CANCELLATION_RULE}/cancellationRuleDetails/${applyRowFn(cellValues)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('priorityRank', hiddenColumns)
            },
            {
                field: 'cancellationType',
                headerName: intl.formatMessage(messages.cancellationType),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(cellValues)?.cancellationType?.name}
                        link={`/#/${UrlConstants.CANCELLATION_RULE}/cancellationRuleDetails/${applyRowFn(cellValues)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('cancellationType', hiddenColumns)
            }

        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.id + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default CancellationRuleList;
